// Applies a normalized tall cabinet layout to a loaded Three.js cabinet instance.
//
// Section meshes are identified by name:
//   Door Left / Door Right or Door_Left / Door_Right -> 'box' section
//   Drawer                 -> 'drawer' section
//   Shelf                  -> 'shelf' section
//
// We keep the original section metrics on first discovery so repeated edits
// do not drift after meshes are moved and scaled in place.

import {
  recordOriginalGeometryPositions,
  restoreOriginalGeometry,
} from '../modelGeometry/meshOriginalGeometry';
import {
  applySlotToMeshes,
  buildBoundingBoxInParentSpace,
  recordSectionCapMetrics,
} from './tallCabinetSectionResizer';
import { getTallCabinetHeightDelta } from '../tallCabinetGeometry/tallCabinetHeightResizer';
import {
  applySectionHighlightBoxes,
  SECTION_LAYOUT_ID_TAG,
} from './tallCabinetSectionHighlightBoxes';
import {
  DEFAULT_HIDDEN_OBJECT_NAME_MARKER,
  DEFAULT_INVISIBLE_MESH_TAG,
} from '../BaseModelLoader';
import {
  CABINET_INTERIOR_SECTION_TYPE as SECTION_TYPE
} from '../../../../../../../constants/cabinetInteriorConstants';
import {
  isRuntimeCabinetHandle,
  removeRuntimeCabinetHandles,
} from '../cabinetLoader/cabinetHandleRuntime';

const SECTION_MESH_TAG = 'tallCabinetSection';
const SECTION_CLONE_TAG = 'tallCabinetSectionClone';
const SECTION_RAW_TYPE_TAG = 'tallCabinetSectionRawType';
const SECTION_RENDER_SLOT_TAG = 'tallCabinetSectionRenderSlot';
const INTERIOR_SHELF_TAG = 'tallCabinetInteriorShelf';
const WIDTH_DEPTH_ORIGINAL_GEOMETRY_TAG = 'tallCabinetSizeOriginalGeometry';
const HEIGHT_ORIGINAL_GEOMETRY_TAG = 'tallCabinetHeightOriginalGeometry';
const DOOR_FRONT_MATERIAL_CLONED_TAG = 'tallCabinetDoorFrontMaterialCloned';
const DOOR_PART_ORIGINAL_VISIBILITY_TAG = 'tallCabinetDoorPartOriginalVisibility';
const HANDLE_PLACEMENT_RATIO_TAG = 'tallCabinetHandlePlacementRatio';
const ALIGN = { top: 'top', bottom: 'bottom' };
const SHELF_PLACEMENT = { bottom: 'bottom', center: 'center' };
const EMPTY_SECTION_TYPE = 'empty';
const QUANTITY_SECTION_TYPES = [SECTION_TYPE.shelf, SECTION_TYPE.drawer];
const MAX_SECTION_QUANTITY = 12;
const DRAWER_BASE_OBJECT_NAME = /^Drawer_Base(?:$|[\d_.])/;
const CLONED_OBJECT_NAME = /_clone_\d+/;
const HANDLE_PLACEMENT_NAME = /^(?:Door|Drawer)_Handle(?:\d*|\.\d+)?$/;

function isHandlePlacementObject(object) {
  return Boolean(object && HANDLE_PLACEMENT_NAME.test(object.name || ''));
}

function classifyMeshName(name) {
  if (/^Shelf\d*$/.test(name)) {
    return 'shelf';
  }

  if (/^Drawer\d*$/.test(name) || DRAWER_BASE_OBJECT_NAME.test(name)) {
    return 'drawer';
  }

  if (/^Door[ _]Left(?:\d*|\.\d+)?$/.test(name)) {
    return 'door_left';
  }

  if (/^Door[ _]Right(?:\d*|\.\d+)?$/.test(name)) {
    return 'door_right';
  }

  return null;
}

function recordOriginals(mesh) {
  if (mesh.userData[SECTION_MESH_TAG]) {
    return;
  }

  const box = buildBoundingBoxInParentSpace(mesh);

  mesh.userData[SECTION_MESH_TAG] = true;
  mesh.userData.origPosY = mesh.position.y;
  mesh.userData.origScaleY = mesh.scale.y;
  mesh.userData.origHeight = box.max.y - box.min.y;
  mesh.userData.origMinY = box.min.y;
  mesh.userData.origMaxY = box.max.y;
  mesh.userData.origCenterY = (box.min.y + box.max.y) / 2;

  recordOriginalGeometryPositions(mesh);
  recordSectionCapMetrics(mesh);
}

function getMeshMetrics(mesh) {
  recordOriginals(mesh);
  recordSectionCapMetrics(mesh);

  return {
    minY: mesh.userData.origMinY,
    maxY: mesh.userData.origMaxY,
    centerY: mesh.userData.origCenterY,
  };
}

/**
 * Normalizes the GLB's section meshes into one logical editable section.
 * `sourceObjects` preserve the authored door/drawer subtree for later cloning.
 */
function createGroup(meshes, options = {}) {
  const metrics = meshes.map((mesh) => getMeshMetrics(mesh));
  const minY = Math.min(...metrics.map((metric) => metric.minY));
  const maxY = Math.max(...metrics.map((metric) => metric.maxY));
  const handlePlacements = collectGroupHandlePlacements(meshes, minY, maxY);

  return {
    meshes,
    handlePlacements,
    sourceObjects: options.sourceObjects || [],
    centerY: metrics.reduce((sum, metric) => sum + metric.centerY, 0) / metrics.length,
    minY,
    maxY,
    isClone: meshes.every((mesh) => Boolean(mesh.userData[SECTION_CLONE_TAG])),
    isDefaultInvisible: meshes.every((mesh) => Boolean(mesh.userData[DEFAULT_INVISIBLE_MESH_TAG])),
  };
}

function sortGroupsTopToBottom(groups) {
  return groups.sort((a, b) => {
    if (a.isDefaultInvisible !== b.isDefaultInvisible) {
      return a.isDefaultInvisible ? 1 : -1;
    }

    return b.centerY - a.centerY;
  });
}

/**
 * Cloned sections are real user-visible parts, so they must not inherit the
 * default-hidden template marker from their source mesh names.
 */
function removeInvisibleNameMarker(name) {
  return (name || '')
    .replace(DEFAULT_HIDDEN_OBJECT_NAME_MARKER, '')
    .replace(/[\s_]+$/g, '')
    .trim();
}

function normalizeClonedSectionMesh(clone) {
  clone.traverse((child) => {
    child.name = removeInvisibleNameMarker(child.name);
    delete child.userData[DEFAULT_INVISIBLE_MESH_TAG];
    delete child.userData[DOOR_FRONT_MATERIAL_CLONED_TAG];
    delete child.userData[DOOR_PART_ORIGINAL_VISIBILITY_TAG];
  });
}

/**
 * Section metrics belong to the current clone and transform, not to the source
 * template. Clear them before measuring cloned GLB subtrees.
 */
function clearSectionMetrics(mesh) {
  delete mesh.userData[SECTION_MESH_TAG];
  delete mesh.userData.origPosY;
  delete mesh.userData.origScaleY;
  delete mesh.userData.origHeight;
  delete mesh.userData.origMinY;
  delete mesh.userData.origMaxY;
  delete mesh.userData.origCenterY;
  delete mesh.userData.origGeometryPositions;
  delete mesh.userData.origGeometryMinY;
  delete mesh.userData.origGeometryMaxY;
  // Object3D.clone() copies userData through JSON, which does not preserve the
  // typed arrays used by the procedural geometry resizers. Let each new mesh
  // record its own baseline instead of inheriting an unusable source cache.
  delete mesh.userData[WIDTH_DEPTH_ORIGINAL_GEOMETRY_TAG];
  delete mesh.userData[HEIGHT_ORIGINAL_GEOMETRY_TAG];
}

// Three.js clones userData through JSON, which turns typed-array geometry
// baselines into plain numeric-key objects. Every mesh in a cloned section,
// including child caps, must start from the authored GLB geometry instead of
// inheriting an already-resized source.
function restoreClonedOriginalGeometry(mesh) {
  if (mesh.userData.origGeometryPositions) {
    restoreOriginalGeometry(mesh);
    return;
  }

  restoreOriginalGeometry(mesh, WIDTH_DEPTH_ORIGINAL_GEOMETRY_TAG);
}

function getDrawerBaseObject(mesh) {
  let current = mesh;

  while (current && current.isObject3D) {
    if (DRAWER_BASE_OBJECT_NAME.test(current.name || '')) {
      return current;
    }

    current = current.parent;
  }

  return null;
}

function getObjectRoot(object) {
  let root = object;

  while (root.parent) {
    root = root.parent;
  }

  return root;
}

function getObjectYInSpace(object, objectSpace) {
  const THREE = window.THREE;
  const worldPosition = new THREE.Vector3();

  getObjectRoot(object).updateMatrixWorld(true);
  object.getWorldPosition(worldPosition);
  objectSpace.worldToLocal(worldPosition);

  return worldPosition.y;
}

function collectGroupHandlePlacements(meshes, groupMinY, groupMaxY) {
  const placementObjects = new Set();

  meshes.forEach((mesh) => {
    const placementRoot = getDrawerBaseObject(mesh) || mesh;
    placementRoot.traverse((object) => {
      if (isHandlePlacementObject(object)) {
        placementObjects.add(object);
      }
    });
  });

  const referenceParent = meshes[0] && meshes[0].parent;
  const groupHeight = groupMaxY - groupMinY;
  if (!referenceParent || groupHeight <= 0) {
    return [];
  }

  return Array.from(placementObjects).map((object) => {
    if (typeof object.userData[HANDLE_PLACEMENT_RATIO_TAG] !== 'number') {
      const placementY = getObjectYInSpace(object, referenceParent);
      object.userData[HANDLE_PLACEMENT_RATIO_TAG] = (placementY - groupMinY) / groupHeight;
    }

    return {
      object,
      referenceParent,
    };
  });
}

function applyGroupHandlePlacements(group, slotBottomLocalY, slotHeightLocal) {
  if (!group.handlePlacements || group.handlePlacements.length === 0) {
    return;
  }

  group.handlePlacements.forEach(({object, referenceParent}) => {
    if (!object.parent) {
      return;
    }

    const THREE = window.THREE;
    const worldPosition = new THREE.Vector3();
    const placementRatio = object.userData[HANDLE_PLACEMENT_RATIO_TAG];
    const targetY = slotBottomLocalY + (slotHeightLocal * placementRatio);

    getObjectRoot(object).updateMatrixWorld(true);
    object.getWorldPosition(worldPosition);
    referenceParent.worldToLocal(worldPosition);
    worldPosition.y = targetY;
    referenceParent.localToWorld(worldPosition);
    object.parent.worldToLocal(worldPosition);
    object.position.copy(worldPosition);
    object.updateMatrixWorld(true);
  });
}

function removeNestedDrawerClones(instance) {
  const drawerBases = [];

  instance.traverse((object) => {
    if (DRAWER_BASE_OBJECT_NAME.test(object.name || '')) {
      drawerBases.push(object);
    }
  });

  drawerBases.forEach((drawerBase) => {
    const nestedClones = [];
    drawerBase.traverse((child) => {
      if (child !== drawerBase && CLONED_OBJECT_NAME.test(child.name || '')) {
        nestedClones.push(child);
      }
    });
    nestedClones.forEach((clone) => {
      if (clone.parent) {
        clone.parent.remove(clone);
      }
    });
  });
}

/**
 * Drawers are authored as a Drawer_Base subtree. Treat the full subtree as one
 * section so the box, cap, front, and handle resize and clone together.
 */
function collectDrawerBaseMeshes(mesh) {
  const drawerBase = getDrawerBaseObject(mesh);
  if (!drawerBase) {
    return [mesh];
  }

  const meshes = [];
  drawerBase.traverse((child) => {
    if (
      child.isMesh
      && !isRuntimeCabinetHandle(child)
      && !isHandlePlacementObject(child)
    ) {
      meshes.push(child);
    }
  });

  return meshes.length ? meshes : [mesh];
}

/**
 * Converts semantic GLB mesh names into editable cabinet interior groups.
 * Hidden template sections are kept as clone sources but sorted after visible
 * authored sections so they do not appear in the default sidebar layout.
 */
function collectGroups(instance) {
  const doorLefts = [];
  const doorRights = [];
  const drawers = [];
  const shelves = [];
  const collectedDrawerBaseObjects = new Set();

  // Clean up clone-of-clone descendants left by earlier layout passes before
  // discovering logical groups. Mutating the tree during discovery can make
  // traversal include detached meshes in the same pass.
  removeNestedDrawerClones(instance);

  instance.traverse((child) => {
    if (!child.isMesh) {
      return;
    }

    if (isRuntimeCabinetHandle(child)) {
      return;
    }

    if (isHandlePlacementObject(child)) {
      return;
    }

    if (child.userData[INTERIOR_SHELF_TAG]) {
      return;
    }

    const drawerBaseObject = getDrawerBaseObject(child);
    const rawType = child.userData[SECTION_RAW_TYPE_TAG]
      || (drawerBaseObject ? 'drawer' : classifyMeshName(child.name));
    if (!rawType) {
      return;
    }

    child.userData[SECTION_RAW_TYPE_TAG] = rawType;
    const metrics = getMeshMetrics(child);
    const entry = { mesh: child, centerY: metrics.centerY };

    if (rawType === 'door_left') {
      doorLefts.push(entry);
      return;
    }

    if (rawType === 'door_right') {
      doorRights.push(entry);
      return;
    }

    if (rawType === 'drawer') {
      const drawerBase = drawerBaseObject;
      if (drawerBase) {
        if (collectedDrawerBaseObjects.has(drawerBase)) {
          return;
        }

        collectedDrawerBaseObjects.add(drawerBase);
        drawers.push({
          mesh: collectDrawerBaseMeshes(child),
          centerY: metrics.centerY,
          sourceObjects: [drawerBase],
        });
        return;
      }

      drawers.push(entry);
      return;
    }

    shelves.push(entry);
  });

  const usedRights = new Set();
  const boxes = [];

  doorLefts.sort((a, b) => b.centerY - a.centerY);
  doorLefts.forEach((left) => {
    let best = null;
    let bestDistance = Infinity;

    doorRights.forEach((right) => {
      if (usedRights.has(right.mesh)) {
        return;
      }

      const distance = Math.abs(left.centerY - right.centerY);
      if (distance < bestDistance) {
        bestDistance = distance;
        best = right;
      }
    });

    if (best) {
      usedRights.add(best.mesh);
      boxes.push(createGroup([left.mesh, best.mesh], {
        sourceObjects: [left.mesh, best.mesh],
      }));
      return;
    }

    boxes.push(createGroup([left.mesh], {
      sourceObjects: [left.mesh],
    }));
  });

  doorRights.forEach((right) => {
    if (!usedRights.has(right.mesh)) {
      boxes.push(createGroup([right.mesh], {
        sourceObjects: [right.mesh],
      }));
    }
  });

  return {
    [SECTION_TYPE.box]: sortGroupsTopToBottom(boxes),
    [SECTION_TYPE.drawer]: sortGroupsTopToBottom(drawers.map((drawer) => (
      Array.isArray(drawer.mesh)
        ? createGroup(drawer.mesh, { sourceObjects: drawer.sourceObjects })
        : createGroup([drawer.mesh])
    ))),
    [SECTION_TYPE.shelf]: sortGroupsTopToBottom(shelves.map((shelf) => createGroup([shelf.mesh]))),
  };
}

/**
 * Clones an authored door or drawer subtree for a newly-added section. This
 * preserves child transforms and keeps caps/handles/interior drawer parts
 * attached to the section the user added.
 */
function cloneSourceObjectSection(sourceGroup) {
  const sourceObjects = sourceGroup.sourceObjects || [];
  if (sourceObjects.length === 0) {
    return null;
  }

  const meshes = [];

  sourceObjects.forEach((sourceObject) => {
    if (!sourceObject || !sourceObject.parent) {
      return;
    }

    const clone = sourceObject.clone(true);

    // Handles are live accessories, not part of the authored section template.
    // They are reapplied from workspace resources after a full cabinet reload.
    removeRuntimeCabinetHandles(clone);

    // Never carry descendants left by an earlier clone operation into a new
    // logical section. Only the authored subtree belongs to the template.
    const inheritedClones = [];
    clone.traverse((child) => {
      if (child !== clone && CLONED_OBJECT_NAME.test(child.name || '')) {
        inheritedClones.push(child);
      }
    });
    inheritedClones.forEach((inheritedClone) => {
      if (inheritedClone.parent) {
        inheritedClone.parent.remove(inheritedClone);
      }
    });

    normalizeClonedSectionMesh(clone);
    clone.name = `${clone.name}_clone_${Date.now()}`;
    clone.visible = true;

    clone.traverse((child) => {
      if (!child.isMesh) {
        return;
      }

      child.visible = true;

      if (child.geometry) {
        child.geometry = child.geometry.clone();
        restoreClonedOriginalGeometry(child);
      }

      // The source may currently be fitted to another section. A new section
      // must start from the authored transform or it inherits the source slot's
      // height and later edits make otherwise independent sections affect it.
      if (typeof child.userData.origPosY === 'number') {
        child.position.y = child.userData.origPosY;
      }
      if (typeof child.userData.origScaleY === 'number') {
        child.scale.y = child.userData.origScaleY;
      }

      clearSectionMetrics(child);
      child.userData[SECTION_CLONE_TAG] = true;
      child.userData[SECTION_RAW_TYPE_TAG] = child.userData[SECTION_RAW_TYPE_TAG] || classifyMeshName(child.name);
      delete child.userData[DEFAULT_INVISIBLE_MESH_TAG];

      if (!clone.isMesh || child === clone) {
        meshes.push(child);
      }
    });

    sourceObject.parent.add(clone);
  });

  return meshes.length ? meshes : null;
}

/**
 * Creates the render meshes for an extra section, preferring subtree clones for
 * authored door/drawer assemblies and falling back to mesh-level clones.
 */
function cloneSectionMeshes(sourceGroup) {
  const sourceObjectClones = cloneSourceObjectSection(sourceGroup);
  if (sourceObjectClones) {
    return sourceObjectClones;
  }

  return sourceGroup.meshes.map((sourceMesh) => {
    const clone = sourceMesh.clone(true);
    removeRuntimeCabinetHandles(clone);

    normalizeClonedSectionMesh(clone);
    clone.name = `${clone.name}_clone_${Date.now()}`;
    clone.userData = {
      ...sourceMesh.userData,
      [SECTION_MESH_TAG]: true,
      [SECTION_CLONE_TAG]: true,
      [SECTION_RAW_TYPE_TAG]: sourceMesh.userData[SECTION_RAW_TYPE_TAG] || classifyMeshName(sourceMesh.name),
    };
    delete clone.userData[DEFAULT_INVISIBLE_MESH_TAG];

    if (
      sourceMesh.geometry
      && (
        sourceMesh.userData.origGeometryPositions
        || sourceMesh.userData[WIDTH_DEPTH_ORIGINAL_GEOMETRY_TAG]
      )
    ) {
      clone.geometry = sourceMesh.geometry.clone();
      restoreClonedOriginalGeometry(clone);
    }

    if (typeof sourceMesh.userData.origPosY === 'number') {
      clone.position.y = sourceMesh.userData.origPosY;
    }
    if (typeof sourceMesh.userData.origScaleY === 'number') {
      clone.scale.y = sourceMesh.userData.origScaleY;
    }

    sourceMesh.parent.add(clone);

    return clone;
  });
}

function getZoneReferenceGroups(groups) {
  const originals = Object.values(groups)
    .flat()
    .filter((group) => !group.isClone && !group.isDefaultInvisible);

  if (originals.length > 0) {
    return originals;
  }

  return Object.values(groups).flat();
}

function getLayoutZoneBounds(groups) {
  const referenceGroups = getZoneReferenceGroups(groups);
  if (referenceGroups.length === 0) {
    return null;
  }

  // Use only the original section meshes as hard bounds. The full cabinet GLB
  // includes crown/toe-kick geometry, and letting sections use that space makes
  // resized fronts bleed above and below the cabinet body.
  return {
    top: Math.max(...referenceGroups.map((group) => group.maxY)),
    bottom: Math.min(...referenceGroups.map((group) => group.minY)),
  };
}

function getShelfPlacement(section) {
  if (section.placement === SHELF_PLACEMENT.bottom) {
    return SHELF_PLACEMENT.bottom;
  }

  return SHELF_PLACEMENT.center;
}

function getSectionQuantity(section) {
  const quantity = parseInt(section.quantity, 10);

  if (section.type === SECTION_TYPE.box) {
    return 0;
  }

  if (!QUANTITY_SECTION_TYPES.includes(section.type) || isNaN(quantity) || quantity < 1) {
    return 1;
  }

  return Math.min(quantity, MAX_SECTION_QUANTITY);
}

function buildShelfRenderSlots(section, quantity) {
  const openingHeight = section.height / (quantity + 1);
  const slots = [];

  for (let index = 0; index < quantity; index++) {
    slots.push({
      ...section,
      height: openingHeight,
      placement: SHELF_PLACEMENT.bottom,
      renderSlotId: `${section.id}:${index}`,
    });
  }

  slots.push({
    type: EMPTY_SECTION_TYPE,
    height: openingHeight,
  });

  return slots;
}

function mergeDoorExtensionSections(sections, isDoorExtensionEnabled) {
  if (!isDoorExtensionEnabled) {
    return sections;
  }

  const mergedSections = [];
  let index = 0;

  while (index < sections.length) {
    const section = sections[index];
    const groupId = section.doorExtensionGroupId;
    if (section.type !== SECTION_TYPE.box || section.hasDoor === false || !groupId) {
      mergedSections.push(section);
      index++;
      continue;
    }

    const doorSections = [];
    while (
      index < sections.length
      && sections[index].type === SECTION_TYPE.box
      && sections[index].hasDoor !== false
      && sections[index].doorExtensionGroupId === groupId
    ) {
      doorSections.push(sections[index]);
      index++;
    }

    if (doorSections.length < 2) {
      mergedSections.push(doorSections[0]);
      continue;
    }

    mergedSections.push({
      ...doorSections[0],
      id: doorSections.map((doorSection) => doorSection.id).join(':'),
      height: doorSections.reduce((sum, doorSection) => sum + doorSection.height, 0),
      doorExtensionSections: doorSections,
    });
  }

  return mergedSections;
}

function tagSectionMeshes(meshes, section) {
  meshes.forEach((mesh) => {
    if (section.id) {
      mesh.userData[SECTION_LAYOUT_ID_TAG] = section.id;
    } else {
      delete mesh.userData[SECTION_LAYOUT_ID_TAG];
    }

    if (section.renderSlotId) {
      mesh.userData[SECTION_RENDER_SLOT_TAG] = section.renderSlotId;
    } else {
      delete mesh.userData[SECTION_RENDER_SLOT_TAG];
    }
  });
}

function buildRenderSlots(sections, isDoorExtensionEnabled) {
  return mergeDoorExtensionSections(sections, isDoorExtensionEnabled).reduce((slots, section) => {
    const quantity = getSectionQuantity(section);

    if (section.type === SECTION_TYPE.shelf) {
      if (section.hasDoor === true) {
        slots.push({
          ...section,
          geometryType: SECTION_TYPE.box,
          interiorShelfCount: quantity,
          renderSlotId: `${section.id}:0`,
        });
        return slots;
      }

      return [...slots, ...buildShelfRenderSlots(section, quantity)];
    }

    if (section.type === SECTION_TYPE.box) {
      slots.push({
        ...section,
        interiorShelfCount: quantity,
        renderSlotId: `${section.id}:0`,
      });
      return slots;
    }

    const slotHeight = section.height / quantity;

    for (let index = 0; index < quantity; index++) {
      slots.push({
        ...section,
        height: slotHeight,
        renderSlotId: `${section.id}:${index}`,
      });
    }

    return slots;
  }, []);
}

function cloneDoorFrontMaterials(mesh) {
  if (mesh.userData[DOOR_FRONT_MATERIAL_CLONED_TAG]) {
    return;
  }

  if (Array.isArray(mesh.material)) {
    mesh.material = mesh.material.map((material) => material.clone());
  } else if (mesh.material) {
    mesh.material = mesh.material.clone();
  }

  mesh.userData[DOOR_FRONT_MATERIAL_CLONED_TAG] = true;
}

function setDoorFrontMaterialVisible(mesh, isVisible) {
  cloneDoorFrontMaterials(mesh);

  if (Array.isArray(mesh.material)) {
    mesh.material.forEach((material) => {
      material.visible = isVisible;
    });
    return;
  }

  if (mesh.material) {
    mesh.material.visible = isVisible;
  }
}

function isDoorCapMesh(mesh) {
  return mesh.isMesh && /_Cap(?:\d+)?($|_|\.)/.test(mesh.name || '');
}

/**
 * A door front is the parent mesh of its caps and handle. Keep that parent
 * active for section resizing, hide its own material, and leave only cap
 * descendants visible when the section's Door checkbox is cleared.
 */
function applyCabinetDoorVisibility(meshes, hasDoor) {
  meshes.forEach((doorFront) => {
    doorFront.visible = true;
    setDoorFrontMaterialVisible(doorFront, hasDoor);

    doorFront.traverse((child) => {
      if (child === doorFront || !child.isMesh) {
        return;
      }

      if (hasDoor) {
        const originalVisibility = child.userData[DOOR_PART_ORIGINAL_VISIBILITY_TAG];
        child.visible = typeof originalVisibility === 'boolean' ? originalVisibility : true;
        delete child.userData[DOOR_PART_ORIGINAL_VISIBILITY_TAG];
        return;
      }

      if (typeof child.userData[DOOR_PART_ORIGINAL_VISIBILITY_TAG] !== 'boolean') {
        child.userData[DOOR_PART_ORIGINAL_VISIBILITY_TAG] = child.visible;
      }

      child.visible = isDoorCapMesh(child);
    });
  });
}

function getSectionSourceGroup(allGroups) {
  // A clone is an output instance, never a template. Cloning it recursively
  // copies runtime children and makes drawer appearance depend on edit order.
  return allGroups.find((group) => !group.isClone && group.isDefaultInvisible)
    || allGroups.find((group) => !group.isClone)
    || null;
}

/**
 * Default-hidden groups are source templates only. A saved or newly-added
 * section must render from a clone, otherwise the template itself gets resized
 * and stops being a clean source for future sections.
 */
function getLayoutAssignmentGroup(section, groups, usableGroupsByType, assignedGroups) {
  const geometryType = section.geometryType || section.type;
  const allGroups = groups[geometryType] || [];

  if (!usableGroupsByType[geometryType]) {
    usableGroupsByType[geometryType] = allGroups.filter((group) => !group.isDefaultInvisible);
  }

  const usableGroups = usableGroupsByType[geometryType];
  const stableGroup = usableGroups.find((group) => (
    !assignedGroups.has(group)
    && group.meshes.some((mesh) => mesh.userData[SECTION_RENDER_SLOT_TAG] === section.renderSlotId)
  ));
  if (stableGroup) {
    return stableGroup;
  }

  const availableGroup = usableGroups.find((group) => !assignedGroups.has(group));
  if (availableGroup) {
    return availableGroup;
  }

  const sourceGroup = getSectionSourceGroup(allGroups);
  if (!sourceGroup) {
    return { meshes: [] };
  }

  const clonedGroup = createGroup(cloneSectionMeshes(sourceGroup));
  allGroups.push(clonedGroup);
  usableGroups.push(clonedGroup);

  return clonedGroup;
}

function pushInteriorShelfTasks(interiorShelfTasks, section, slotBottomLocalY, slotHeightLocal) {
  if (section.doorExtensionSections && section.doorExtensionSections.length) {
    let sectionBottomLocalY = slotBottomLocalY;

    [...section.doorExtensionSections].reverse().forEach((doorSection) => {
      const shelfCount = getSectionQuantity(doorSection);
      const sectionHeightLocal = (doorSection.height / section.height) * slotHeightLocal;

      if (shelfCount > 0) {
        interiorShelfTasks.push({
          count: shelfCount,
          slotBottomLocalY: sectionBottomLocalY,
          slotHeightLocal: sectionHeightLocal,
          sectionId: doorSection.id,
        });
      }

      sectionBottomLocalY += sectionHeightLocal;
    });
    return;
  }

  if (section.interiorShelfCount > 0) {
    interiorShelfTasks.push({
      count: section.interiorShelfCount,
      slotBottomLocalY,
      slotHeightLocal,
      sectionId: section.id,
    });
  }
}

// A shelf section represents empty vertical space with a thin board inside it.
// Do not stretch Shelf meshes to the section height like doors and drawers.
function applyShelfToMeshes(meshes, slotBottomLocalY, slotHeightLocal, placement) {
  if (!meshes.length) {
    return;
  }

  meshes.forEach((mesh) => {
    const origHeight = mesh.userData.origHeight;
    if (!origHeight || origHeight <= 0) {
      return;
    }

    const scaleY = Math.min(1, slotHeightLocal / origHeight);
    const shelfHeightLocal = origHeight * scaleY;
    const origScaleY = mesh.userData.origScaleY || 1;
    const origPosY = mesh.userData.origPosY || 0;
    const offsetToBottom = mesh.userData.origMinY - origPosY;
    // Shelf section slots use their bottom edge as the board centerline between openings.
    const shelfBottomLocalY = placement === SHELF_PLACEMENT.bottom
      ? slotBottomLocalY - (shelfHeightLocal / 2)
      : slotBottomLocalY + ((slotHeightLocal - shelfHeightLocal) / 2);

    restoreOriginalGeometry(mesh);
    mesh.scale.y = origScaleY * scaleY;
    mesh.position.y = shelfBottomLocalY - (offsetToBottom * scaleY);
    mesh.visible = true;
  });
}

// Places shelf boards inside cabinet sections (behind the doors).
// `tasks` = [{count, slotBottomLocalY, slotHeightLocal}] collected during the main layout pass.
function applyAllInteriorShelves(instance, shelfSourceGroups, tasks) {
  const totalNeeded = tasks.reduce((sum, t) => sum + t.count, 0);

  if (!instance.userData[INTERIOR_SHELF_TAG]) {
    instance.userData[INTERIOR_SHELF_TAG] = [];
  }
  const pool = instance.userData[INTERIOR_SHELF_TAG];

  // Hide everything in the pool first; we'll re-show what we need.
  pool.forEach((g) => g.meshes.forEach((m) => { m.visible = false; }));

  if (totalNeeded === 0) return;

  const shelfSource = getSectionSourceGroup(shelfSourceGroups);
  if (!shelfSource) return;

  while (pool.length < totalNeeded) {
    const clones = cloneSectionMeshes(shelfSource);
    const newGroup = createGroup(clones);
    clones.forEach((m) => { m.userData[INTERIOR_SHELF_TAG] = true; });
    pool.push(newGroup);
  }

  let poolIndex = 0;
  const shelfOrigHeight = (shelfSource.meshes[0] && shelfSource.meshes[0].userData.origHeight) || 0.5;

  tasks.forEach(({ count, slotBottomLocalY, slotHeightLocal, sectionId }) => {
    const spacing = slotHeightLocal / (count + 1);

    for (let i = 0; i < count; i++) {
      if (poolIndex >= pool.length) break;
      const group = pool[poolIndex++];
      // Center the shelf board at the evenly-spaced position; use origHeight*2 so scale stays 1.
      const shelfCenterY = slotBottomLocalY + spacing * (i + 1);
      applyShelfToMeshes(group.meshes, shelfCenterY - shelfOrigHeight, shelfOrigHeight * 2, SHELF_PLACEMENT.center);
      tagSectionMeshes(group.meshes, { id: sectionId });
    }
  });
}

/**
 * Apply a tall cabinet layout to a loaded Three.js cabinet instance.
 *
 * @param {THREE.Object3D} instance
 * @param {{alignment: string, sections: Array<{type: string, height: number, quantity?: number, placement?: string}>}} layout
 */
export function applyTallCabinetLayout(instance, layout) {
  if (!instance || !layout || !Array.isArray(layout.sections) || layout.sections.length === 0) {
    return;
  }

  const sections = layout.sections.filter((section) => section && section.height > 0);
  if (sections.length === 0) {
    return;
  }

  const groups = collectGroups(instance);
  const layoutZoneBounds = getLayoutZoneBounds(groups);
  if (!layoutZoneBounds) {
    return;
  }

  layoutZoneBounds.top += getTallCabinetHeightDelta(instance);

  const layoutZoneHeight = layoutZoneBounds.top - layoutZoneBounds.bottom;
  if (layoutZoneHeight <= 0) {
    return;
  }

  const totalSectionHeight = sections.reduce((sum, section) => sum + section.height, 0);
  if (totalSectionHeight <= 0) {
    return;
  }

  const renderSlots = buildRenderSlots(sections, Boolean(layout.doorExtensionEnabled));
  if (renderSlots.length === 0) {
    return;
  }

  const interiorShelfTasks = [];

  const cabinetHeight = instance.userData.cabinetInteriorHeight || instance.userData.shapeHeight || totalSectionHeight;
  const heightBasis = Math.max(cabinetHeight, totalSectionHeight);
  const usedLocalHeight = (totalSectionHeight / heightBasis) * layoutZoneHeight;
  const freeLocalHeight = Math.max(0, layoutZoneHeight - usedLocalHeight);
  const isBottomAligned = layout.alignment === ALIGN.bottom;

  const usableGroupsByType = {};
  const assignedGroups = new Set();
  const assignments = renderSlots.map((section) => {
    if (section.type === EMPTY_SECTION_TYPE) {
      return { section, group: { meshes: [] } };
    }

    const group = getLayoutAssignmentGroup(section, groups, usableGroupsByType, assignedGroups);
    assignedGroups.add(group);

    return { section, group };
  });

  Object.values(groups).forEach((availableGroups) => {
    availableGroups.forEach((group) => {
      const isUsed = assignedGroups.has(group);
      group.meshes.forEach((mesh) => {
        mesh.visible = isUsed;
        if (!isUsed) {
          delete mesh.userData[SECTION_LAYOUT_ID_TAG];
        }
      });
      group.handlePlacements.forEach(({object}) => {
        object.visible = isUsed;
      });
    });
  });

  let currentBottomLocalY = isBottomAligned
    ? layoutZoneBounds.bottom
    : layoutZoneBounds.bottom + freeLocalHeight;

  [...assignments].reverse().forEach(({ section, group }) => {
    const slotHeightLocal = (section.height / heightBasis) * layoutZoneHeight;

    switch (section.type) {
      case EMPTY_SECTION_TYPE:
        pushInteriorShelfTasks(interiorShelfTasks, section, currentBottomLocalY, slotHeightLocal);
        break;
      case SECTION_TYPE.shelf:
        tagSectionMeshes(group.meshes, section);
        if (section.hasDoor === true) {
          applySlotToMeshes(group.meshes, currentBottomLocalY, slotHeightLocal);
          applyCabinetDoorVisibility(group.meshes, true);
          pushInteriorShelfTasks(interiorShelfTasks, section, currentBottomLocalY, slotHeightLocal);
        } else {
          applyShelfToMeshes(group.meshes, currentBottomLocalY, slotHeightLocal, getShelfPlacement(section));
        }
        break;
      case SECTION_TYPE.box:
        tagSectionMeshes(group.meshes, section);
        applySlotToMeshes(group.meshes, currentBottomLocalY, slotHeightLocal);
        applyCabinetDoorVisibility(group.meshes, section.hasDoor !== false);
        pushInteriorShelfTasks(interiorShelfTasks, section, currentBottomLocalY, slotHeightLocal);
        break;
      default:
        tagSectionMeshes(group.meshes, section);
        applySlotToMeshes(group.meshes, currentBottomLocalY, slotHeightLocal);
    }

    applyGroupHandlePlacements(group, currentBottomLocalY, slotHeightLocal);

    currentBottomLocalY += slotHeightLocal;
  });

  applyAllInteriorShelves(instance, groups[SECTION_TYPE.shelf], interiorShelfTasks);
}

export function applyTallCabinetSectionHighlights(instance, layout) {
  if (!instance || !layout || !Array.isArray(layout.sections) || layout.sections.length === 0) {
    return;
  }

  const sections = layout.sections.filter((section) => section && section.height > 0);
  if (sections.length === 0) {
    return;
  }

  const groups = collectGroups(instance);
  const layoutZoneBounds = getLayoutZoneBounds(groups);
  if (!layoutZoneBounds) {
    return;
  }

  layoutZoneBounds.top += getTallCabinetHeightDelta(instance);

  const layoutZoneHeight = layoutZoneBounds.top - layoutZoneBounds.bottom;
  if (layoutZoneHeight <= 0) {
    return;
  }

  const totalSectionHeight = sections.reduce((sum, section) => sum + section.height, 0);
  if (totalSectionHeight <= 0) {
    return;
  }

  const cabinetHeight = instance.userData.cabinetInteriorHeight || instance.userData.shapeHeight || totalSectionHeight;
  const heightBasis = Math.max(cabinetHeight, totalSectionHeight);
  const usedLocalHeight = (totalSectionHeight / heightBasis) * layoutZoneHeight;
  const freeLocalHeight = Math.max(0, layoutZoneHeight - usedLocalHeight);
  const isBottomAligned = layout.alignment === ALIGN.bottom;
  const currentBottomLocalY = isBottomAligned
    ? layoutZoneBounds.bottom
    : layoutZoneBounds.bottom + freeLocalHeight;

  applySectionHighlightBoxes(
    instance,
    groups,
    sections,
    currentBottomLocalY,
    heightBasis,
    layoutZoneHeight
  );
}
